import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useDocNotFound } from '@/shared/utils/docNotFound';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { leaveSession } from '@/auth/store';
import { logout as apiLogout, getMe } from '@/api/auth';
import { useAccessMode } from '@/store/accessMode';
import HeaderHelpSearch, { HeaderHelpButton } from '@/shared/components/HeaderHelpSearch';
import HeaderIconActions, { HeaderLogoutButton } from '@/shared/components/HeaderIconActions';
import UserMenu from '@/shared/components/UserMenu';
import { useQuery } from '@tanstack/react-query';
import TenantSelect from '@/components/TenantSelect';
import SupportWidget from '@/shared/components/SupportWidget';
import HeaderDefaults from '@/components/HeaderDefaults';
import BillingNotices from '@/components/BillingNotices';
import ModeSwitch from '@/components/ModeSwitch';
import { useOnboarding } from '@/hooks/useOnboarding';
import { hasModule, useEnabledModules } from '@/hooks/useEnabledModules';
import { usePermissions } from '@/auth/usePermissions';
import AiBadge from '@/shared/components/AiBadge';
import AutoRouteGuard from '@/auth/AutoRouteGuard';
import ErrorBoundary from '@/components/ErrorBoundary';
import OpenTabsBar from '@/shared/components/OpenTabsBar';
import uiPrefs from '@/store/uiPrefs';
import { dropLegacyStickyFilters } from '@/hooks/useStickyFilters';
import BrandLogo from '@/shared/components/BrandLogo';
import {
  I18N_PREFIX,
  SIDEBAR_ITEMS,
  type SidebarItem,
} from './sidebarItems';

function resolveLabel(label: string, t: (key: string) => string): string {
  if (label.startsWith(I18N_PREFIX)) {
    return t(label.slice(I18N_PREFIX.length));
  }
  return label;
}

/**
 * Фільтрує SIDEBAR_ITEMS:
 *   - за enabled_modules (3.0.5.1c): link з module=null лишається,
 *     link з module що НЕ у Set — приховується.
 *   - за permissions (3.0.5.5): link з permission, якого user не має —
 *     приховується. OWNER/ADMIN бачать усе.
 *
 * Sections, після яких не лишилось видимих items до наступної section,
 * приховуються автоматично.
 */
export function filterSidebarItems(
  items: SidebarItem[],
  modulesState: ReturnType<typeof useEnabledModules>,
  permissions: ReturnType<typeof usePermissions>,
): SidebarItem[] {
  // 1. Видаляємо приховані link'и.
  const visibleLinks = items.filter((it) => {
    if (it.type !== 'link') return true; // section проходимо у крок 2
    // module filter
    if (it.module !== null && !hasModule(modulesState, it.module)) return false;
    // permission filter
    // Поки me ще loading — показуємо все (fast path для OWNER/ADMIN
    // спрацьовує тільки коли me.data є; до цього кнопки/links не мерехтять).
    if (permissions.isLoading) return true;
    if (it.permission === null) return true;
    return permissions.has(it.permission);
  });
  // 2. Видаляємо section'и (і ч.952 — subsection'и), після яких нема link'ів.
  const result: SidebarItem[] = [];
  for (let i = 0; i < visibleLinks.length; i++) {
    const item = visibleLinks[i]!;
    if (item.type === 'section' || item.type === 'subsection') {
      // Чи є хоча б один link далі — до наступного заголовка того ж рівня.
      // Для section межа — наступна section; для subsection — будь-який
      // заголовок (section або subsection).
      let hasLinkAfter = false;
      for (let j = i + 1; j < visibleLinks.length; j++) {
        const next = visibleLinks[j]!;
        if (next.type === 'section') break;
        if (next.type === 'subsection' && item.type === 'subsection') break;
        if (next.type === 'link') {
          hasLinkAfter = true;
          break;
        }
      }
      if (hasLinkAfter) result.push(item);
    } else {
      result.push(item);
    }
  }
  return result;
}

/**
 * Чи лежить шлях `pathname` всередині пункту `to` ПО МЕЖI СЕГМЕНТА.
 * Точний збіг або вкладений шлях (`to` + '/'). НЕ голий startsWith — щоб
 * '/positions' не збігся з '/pos'.
 */
export function pathMatches(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + '/');
}

/**
 * ч.952: елемент субменю — або посилання, або ПІДЗАГОЛОВОК-роздільник
 * (`kind: 'subsection'`, без `to`).
 */
interface MenuLink {
  /** ч.952: 'subsection' — роздільник із назвою (не клікабельний). */
  kind?: 'subsection';
  to: string;
  label: string;
  /** Сторінка з агентом Oblik+/AI — біля назви бейдж «ШІ». */
  ai?: boolean;
  match?: string[];   // додаткові префікси для активації (редактори з власним URL)
  // Шляхи, які НЕ підсвічують цей пункт, хоча й лежать «всередині» to.
  // Потрібно, коли вкладений шлях має ВЛАСНИЙ пункт меню (інакше підсвітились
  // би обидва): «Кабінет» /dps-cabinet vs «Створити документ» /dps-cabinet/new.
  exclude?: string[];
}
interface MenuGroup {
  label: string;       // вже resolved
  links: MenuLink[];
}

/** Чи активний пункт меню для поточного шляху: збіг по `to` АБО по будь-якому
 * `match`, окрім шляхів з `exclude` (вони мають власний пункт меню). */
export function linkMatches(pathname: string, l: MenuLink): boolean {
  // ч.953 (баг власника): підзаголовок-роздільник (ч.952) має ПОРОЖНІЙ `to`,
  // а pathMatches(x, '') — завжди true (бо x.startsWith('/')). Через це розділ
  // з роздільниками («Товари») ставав «активним» на БУДЬ-ЯКІЙ сторінці і ліве
  // субменю не перемикалось (напр. на /nomenclature-specifications з
  // «Виробництва»). Роздільник ніколи не активує розділ.
  if (l.kind === 'subsection' || !l.to) return false;
  if ((l.exclude ?? []).some((e) => pathMatches(pathname, e))) return false;
  if (pathMatches(pathname, l.to)) return true;
  return (l.match ?? []).some((m) => pathMatches(pathname, m));
}

/** Підсвітка пункту меню. ч.1392: власна підсвітка NavLink спрацьовує за префіксом адреси
 * («Кабінет ДПС» /dps-cabinet на /dps-cabinet/new) і не знає про `exclude` — тоді світились
 * обидва пункти. Шлях з `exclude` пункт не підсвічує ніколи. */
export function navLinkActive(isActive: boolean, pathname: string, l: MenuLink): boolean {
  if ((l.exclude ?? []).some((e) => pathMatches(pathname, e))) return false;
  return isActive || linkMatches(pathname, l);
}

/**
 * Розділ для ЛІВОГО субменю: той, у якому лежить поточна сторінка. В
 * онбордингу (акаунт без організацій у режимі «Користувач») субменю немає.
 * ч.1087 (вимога власника): майстер створення організації (/organizations/new)
 * теж показує субменю — раніше (ч.1072) сторінка була «на всю ширину» без нього.
 */
export function submenuGroupFor(
  pathname: string, groups: MenuGroup[], onboarding: boolean,
): MenuGroup | null {
  if (onboarding) return null;
  return groups.find((g) => g.links.some((l) => linkMatches(pathname, l))) ?? null;
}

/**
 * Згрупувати плаский visibleItems (section-маркери + links) у розділи верхнього
 * меню. Links ДО першої section (Головна) — у безсекційну групу з порожнім label
 * (рендериться окремим пунктом, не dropdown).
 */
export function groupForTopMenu(
  items: SidebarItem[],
  t: (key: string) => string,
): { standalone: MenuLink[]; groups: MenuGroup[] } {
  const standalone: MenuLink[] = [];
  const groups: MenuGroup[] = [];
  let current: MenuGroup | null = null;
  for (const it of items) {
    if (it.type === 'section') {
      current = { label: resolveLabel(it.label, t), links: [] };
      groups.push(current);
    } else if (it.type === 'subsection') {
      // ч.952: роздільник усередині розділу (не створює новий розділ).
      if (current) current.links.push({ kind: 'subsection', to: '', label: resolveLabel(it.label, t) });
    } else if (current) {
      current.links.push({ to: it.to, label: resolveLabel(it.label, t), match: it.match, exclude: it.exclude, ai: it.ai });
    } else {
      standalone.push({ to: it.to, label: resolveLabel(it.label, t), match: it.match, exclude: it.exclude, ai: it.ai });
    }
  }
  // Прибрати порожні групи (фільтр міг лишити section без links — підстраховка).
  return {
    standalone,
    groups: groups.filter((g) => g.links.some((l) => l.kind !== 'subsection')),
  };
}

/**
 * ч.1063 (вимога власника «убери скрол з випадайки»): пункти розділу —
 * блоками «підзаголовок + його пункти», щоб при розкладці на кілька колонок
 * підрозділ не розривався між колонками.
 */
export function menuBlocks(links: MenuLink[]): { header: MenuLink | null; links: MenuLink[] }[] {
  const out: { header: MenuLink | null; links: MenuLink[] }[] = [];
  for (const l of links) {
    if (l.kind === 'subsection') out.push({ header: l, links: [] });
    else if (out.length === 0) out.push({ header: null, links: [l] });
    else out[out.length - 1]!.links.push(l);
  }
  return out;
}

/** Скільки колонок потрібно, щоб випадайка вмістилась по висоті без скролу. */
export function dropdownColumns(natural: number, available: number, maxCols = 6): number {
  if (available <= 0 || natural <= available) return 1;
  return Math.min(maxCols, Math.ceil(natural / available));
}

/**
 * ч.1063: випадайка БЕЗ скролу. Не вміщується у висоту вікна — розкладається
 * на кілька колонок (CSS columns); не вміщується праворуч — зсувається ліворуч.
 * Вимірювання — до відмальовки (useLayoutEffect), тож без мерехтіння.
 */
function fitDropdown(el: HTMLElement): void {
  const TOL = 3;   // рамка/округлення — не привід для колонок чи прокрутки
  el.style.columnCount = '';
  el.style.width = '';
  el.style.left = '';
  el.style.maxHeight = '';
  el.style.overflowY = '';
  const blocks = Array.from(el.querySelectorAll<HTMLElement>('.app__topnav-block'));
  blocks.forEach((b) => { b.style.breakInside = ''; });
  const rect = el.getBoundingClientRect();
  const available = window.innerHeight - rect.top - 12;
  const colWidth = rect.width;
  const height = () => el.getBoundingClientRect().height;
  const apply = (n: number) => {
    el.style.columnCount = n > 1 ? String(n) : '';
    el.style.width = n > 1 ? `${Math.ceil(colWidth * n)}px` : '';
  };
  if (rect.height > available + TOL) {
    const maxByWidth = Math.max(1, Math.floor((window.innerWidth - 16) / Math.max(colWidth, 1)));
    // Блок, вищий за колонку (розділ без підзаголовків — увесь список), рвемо —
    // інакше колонки не допоможуть.
    blocks.forEach((b) => {
      if (b.getBoundingClientRect().height > available - TOL) b.style.breakInside = 'auto';
    });
    let cols = Math.min(dropdownColumns(rect.height, available), maxByWidth);
    apply(cols);
    // Цілі підрозділи не рвуться між колонками — інколи потрібна ще одна
    // колонка; додаємо, лише доки це справді зменшує висоту.
    let h = height();
    while (h > available + TOL && cols < maxByWidth) {
      apply(cols + 1);
      const h2 = height();
      if (h2 >= h - 1) { apply(cols); break; }
      cols += 1;
      h = h2;
    }
    // Не вмістились цілими підрозділами — дозволяємо рвати й їх.
    if (height() > available + TOL) {
      blocks.forEach((b) => { b.style.breakInside = 'auto'; });
    }
    // Крайній випадок (крихітне вікно): лише тоді — прокрутка, щоб пункти
    // лишались досяжними.
    if (height() > available + TOL) {
      el.style.maxHeight = `${Math.max(available, 120)}px`;
      el.style.overflowY = 'auto';
    }
  }
  const r = el.getBoundingClientRect();
  const overflow = r.right - (window.innerWidth - 8);
  if (overflow > 0) el.style.left = `${-Math.min(overflow, Math.max(0, r.left - 8))}px`;
}

/**
 * ч.1000: `children` — необовʼязковий. Зазвичай макет наповнює `<Outlet/>`
 * (вкладені маршрути). Але довідка має ОДИН маршрут на гостя і залогіненого
 * (`/help`), тому вона сама вирішує оболонку і передає вміст як children.
 */
export default function AppLayout({ children }: { children?: ReactNode } = {}) {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  // ч.1119: усі ролі користувача «лише перегляд» — кнопки дій у документах
  // вимикаються (DocumentToolbar читає стор), у шапці — позначка.
  const readOnly = me.data?.read_only === true;
  // ч.1312: документ сторінки не знайдено (404) — плашка над сторінкою.
  const docNotFound = useDocNotFound();
  const setReadOnly = useAccessMode((s) => s.setReadOnly);
  useEffect(() => { setReadOnly(readOnly); }, [readOnly, setReadOnly]);
  const modulesState = useEnabledModules();
  const permissions = usePermissions();
  const location = useLocation();
  // Відкритий dropdown зверху (клік по розділу). Працює НЕЗАЛЕЖНО від лівого
  // субменю — щоб підпункти були доступні навіть коли ліве згорнуте.
  const [dropdownGroup, setDropdownGroup] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  // Закрити dropdown при зміні маршруту (перейшли на сторінку).
  useEffect(() => { setDropdownGroup(null); }, [location.pathname]);
  // ч.1063: випадайка без скролу — колонки/зсув за розміром вікна.
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = dropdownRef.current;
    if (dropdownGroup === null || !el) return undefined;
    const fit = () => fitDropdown(el);
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [dropdownGroup]);
  // Закрити dropdown при кліку поза меню.
  useEffect(() => {
    if (dropdownGroup === null) return;
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setDropdownGroup(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [dropdownGroup]);
  // Ліве субменю: ширина (ресайз) + згорнутість — persist у localStorage.
  const [subWidth, setSubWidth] = useState<number>(() => {
    const v = Number(localStorage.getItem('submenu.width'));
    return v >= 160 && v <= 420 ? v : 220;
  });
  const [subCollapsed, setSubCollapsed] = useState<boolean>(
    () => localStorage.getItem('submenu.collapsed') === '1');

  useEffect(() => { localStorage.setItem('submenu.width', String(subWidth)); }, [subWidth]);
  useEffect(() => {
    localStorage.setItem('submenu.collapsed', subCollapsed ? '1' : '0');
  }, [subCollapsed]);

  async function handleLogout() {
    try { await apiLogout(); } catch { /* вихід однаково */ }
    // ч.1229: вихід — з повним перезавантаженням (кеш запитів і сховища сторінки
    // не переходять до наступного користувача цієї вкладки).
    leaveSession();
  }

  const visibleItems = filterSidebarItems(SIDEBAR_ITEMS, modulesState, permissions);
  const { standalone, groups } = groupForTopMenu(visibleItems, t);

  // ЛIВЕ субменю слідує за МАРШРУТОМ (де юзер реально перебуває), а НЕ за кліком на
  // розділ. Клік на розділ відкриває лише dropdown зверху (dropdownGroup); ліва
  // панель оновлюється тільки коли юзер перейшов у підпункт цього розділу.
  //
  // Збіг шляху рахуємо ПО МЕЖI СЕГМЕНТА (не голий startsWith): шлях або точно
  // дорівнює link, або починається з `link + '/'`. Інакше '/positions' хибно
  // збігся б з '/pos' (POS-термінал у розділі Роздріб) і ліве меню стрибало б
  // на чужий розділ.
  // `/` (Загальне) тепер пункт групи «Головна» — НЕ виключаємо його, інакше на
  // головній сторінці ліве субменю не показувалось би. pathMatches для to='/'
  // дає true ЛИШЕ при точному pathname==='/' (startsWith('//') ніколи), тож
  // '/' не «з'їдає» інші сторінки.
  const activeGroup = submenuGroupFor(location.pathname, groups, false);

  // ч.1072 (пропозиція власника): поки в акаунті немає доступних організацій —
  // без меню, фільтрів і вкладок; у шапці замість фільтрів «додати організацію».
  const { onboarding, edoOnly } = useOnboarding();
  // Майстер створення організації: в онбордингу у шапці не показуємо заклик
  // «додати організацію» — людина вже на цій сторінці. ч.1087: ліве субменю
  // на майстрі є (як на будь-якій сторінці розділу).
  const focusPage = location.pathname === '/organizations/new';
  const submenuGroup = submenuGroupFor(location.pathname, groups, onboarding || edoOnly);

  // Налаштування UI з профілю (2026-08-26): тягнемо після логіну; сервер
  // головніший за localStorage-дзеркало (інший компʼютер/браузер).
  useEffect(() => { void uiPrefs.init().then(dropLegacyStickyFilters); }, []);

  return (
    <div className={`app${subCollapsed || !submenuGroup ? ' app--sub-collapsed' : ''}${
      onboarding || edoOnly ? ' app--onboarding' : ''}`}
         style={!subCollapsed && submenuGroup
           ? { ['--sub-w' as string]: `${subWidth}px` } : undefined}>
      <header className="app__header">
        {/* ч.1229: логотип замість напису; клік — на головну. */}
        <h1 className="app__brand">
          <NavLink to="/" title={t('app.title')}><BrandLogo variant="header" /></NavLink>
        </h1>
        {/* 2026-07-25: організація + період за замовчуванням (store/defaults).
            ч.1072: без організацій фільтрам нема що фільтрувати — на їхньому
            місці заклик додати організацію. */}
        {/* ч.1075: фільтр клієнта (кілька акаунтів) — перед організацією; у
            HeaderDefaults він уже є, тут — для акаунта без організацій. */}
        {/* ч.1403 (пропозиція власника): перемикач режиму іконками — після логотипа, перед організаціями. */}
        <ModeSwitch />
        {onboarding
          ? (!focusPage && <TenantSelect />)
          : <HeaderDefaults />}
        <div className="right">
          {/* ч.1000 (запит власника): пошук по довідці — ПЕРЕД іконкою
              масштабу шрифта; знак питання — перехід у довідник. */}
          <HeaderHelpSearch />
          <HeaderHelpButton />
          {/* ч.962 (запит власника): масштаб шрифта + день/ніч — іконками
              ліворуч від назви тенанта; «вийти» — теж іконкою. */}
          <HeaderIconActions />
          {readOnly && (
            <span className="app__readonly" data-testid="read-only-badge"
              title="Ваша роль дозволяє лише перегляд: створювати, змінювати і проводити документи не можна. Ширший доступ надає адміністратор акаунта.">
              лише перегляд
            </span>
          )}
          <UserMenu me={me.data} />
          {/* ч.963: «вийти» — скраю екрана, після блоку облікового запису. */}
          <HeaderLogoutButton onLogout={handleLogout} />
        </div>
      </header>

      {/* Верхні розділи: клік відкриває dropdown зверху (працює завжди, навіть коли
          ліве згорнуте) ТА робить розділ активним для лівого субменю. */}
      {/* ч.1401 → ч.1406: режим «ЕДО» — замість повного меню лише документообіг
          («Баланс і тарифи» — в обліковому записі, рішення власника). */}
      {edoOnly && (
        <nav className="app__topnav" aria-label="Електронні документи">
          <NavLink to="/edo/journal" className="app__topnav-link">Журнал ЕДО</NavLink>
          {/* ч.1435 (рішення власника): учасники ЕДО — той самий список, що «Організації» в «Бізнесі». */}
          <NavLink to="/organizations"
            className={({ isActive }) => `app__topnav-link${
              isActive || location.pathname.startsWith('/edo/cards') ? ' active' : ''}`}>
            Учасники ЕДО
          </NavLink>
        </nav>
      )}
      {!onboarding && !edoOnly && (
      <nav className="app__topnav" ref={navRef}>
        {standalone.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.to === '/'} className="app__topnav-link">
            {l.label}
          </NavLink>
        ))}
        {groups.map((g) => {
          const isDropdownOpen = dropdownGroup === g.label;
          return (
            <div key={g.label} className="app__topnav-group">
              <button
                type="button"
                className={`app__topnav-trigger${
                  isDropdownOpen || activeGroup?.label === g.label ? ' is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
                onClick={() => setDropdownGroup(isDropdownOpen ? null : g.label)}
              >
                {g.label}
                <span className="app__topnav-caret">▾</span>
              </button>
              <div className={`app__topnav-dropdown${isDropdownOpen ? ' is-open' : ''}`}
                   ref={isDropdownOpen ? dropdownRef : undefined}
                   role="menu">
                {/* ч.1063: блоки «підзаголовок + пункти» — не рвуться між колонками. */}
                {menuBlocks(g.links).map((b, bi) => (
                  <div key={`blk-${bi}`} className="app__topnav-block">
                    {/* ч.952: підзаголовок-роздільник усередині розділу */}
                    {b.header && <div className="app__topnav-subsection">{b.header.label}</div>}
                    {b.links.map((l) => (
                      <NavLink key={l.to} to={l.to} role="menuitem"
                               className={({ isActive }) =>
                                 `app__topnav-item${navLinkActive(isActive, location.pathname, l) ? ' active' : ''}`}
                               onClick={() => setDropdownGroup(null)}>
                        {l.label}{l.ai && <AiBadge />}
                      </NavLink>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      )}

      {/* Ліве субменю — підпункти активного розділу, ресайзбл + згортання. */}
      {submenuGroup && !subCollapsed && (
        <aside className="app__submenu" style={{ width: subWidth }}>
          <div className="app__submenu-head">
            <span>{submenuGroup.label}</span>
            <button type="button" className="app__submenu-toggle"
                    title="Згорнути" onClick={() => setSubCollapsed(true)}>‹</button>
          </div>
          <div className="app__submenu-items">
            {submenuGroup.links.map((l, i) => (l.kind === 'subsection' ? (
              /* ч.952: підзаголовок-роздільник усередині розділу */
              <div key={`sub-${i}`} className="app__submenu-subsection">{l.label}</div>
            ) : (
              <NavLink key={l.to} to={l.to}
                       className={({ isActive }) =>
                         `app__submenu-item${navLinkActive(isActive, location.pathname, l) ? ' active' : ''}`}>
                {l.label}{l.ai && <AiBadge />}
              </NavLink>
            )))}
          </div>
          {/* Ресайз-ручка */}
          <div
            className="app__submenu-resizer"
            onMouseDown={(e) => {
              e.preventDefault();
              const startX = e.clientX;
              const startW = subWidth;
              const onMove = (ev: MouseEvent) => {
                const w = Math.min(420, Math.max(160, startW + ev.clientX - startX));
                setSubWidth(w);
              };
              const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            }}
          />
        </aside>
      )}

      {/* Кнопка розгорнути субменю (коли згорнуте, але розділ активний) */}
      {submenuGroup && subCollapsed && (
        <button type="button" className="app__submenu-expand"
                title={`Показати: ${submenuGroup.label}`}
                onClick={() => setSubCollapsed(false)}>›</button>
      )}

      {/* Вкладки відкритих сторінок (2026-08-24) — між меню і сторінкою. */}
      {!onboarding && !edoOnly && <OpenTabsBar />}

      <main className="app__main">
        {docNotFound && (
          <div className="mc__panel mc__panel--info" role="status" data-testid="doc-not-found"
            style={{ margin: '0.75rem 1rem 0' }}>
            Документ не знайдено: його видалено, або він вам недоступний — наприклад, роль
            показує лише записи, створені вами. Перевірте посилання або зверніться до
            адміністратора акаунта.
          </div>
        )}
        {/* ч.1398: стан оплати баз — лише коли є на що звернути увагу. */}
        {!onboarding && <BillingNotices />}
        <AutoRouteGuard>
          <ErrorBoundary>
            {children ?? <Outlet />}
          </ErrorBoundary>
        </AutoRouteGuard>
      </main>

      {/* ч.1011: віджет підтримки — кругла кнопка «?» праворуч унизу,
          висить поверх будь-якої сторінки (приклад власника). */}
      <SupportWidget />
    </div>
  );
}
